import { Fragment, useEffect, useMemo, useState } from 'react'

import {
  AppstoreOutlined,
  PlusOutlined,
  CopyOutlined,
  FileProtectOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import { Button, Tooltip, Typography, Tag, Progress } from 'antd'
import type { ColumnsType } from 'antd/es/table'

import {
  PageContainer,
  PageHeader,
  PageSearchToolbar,
  PageFooter,
  PageGrid,
  PageGridPagination,
  type RefreshOption
} from '../../components/page'
import { IconButton } from '../../components/ui'
import hljs from 'highlight.js/lib/core'
import typescript from 'highlight.js/lib/languages/typescript'

hljs.registerLanguage('typescript', typescript)

const { Title, Paragraph, Text } = Typography

const searchRefreshOptions: RefreshOption[] = [
  { label: 'Every 30 seconds', value: 30_000 },
  { label: 'Every minute', value: 60_000 },
  { label: 'Every 5 minutes', value: 300_000 },
  { label: 'Every 30 minutes', value: 1_800_000 }
]

const headingFontFamily = '"JetBrains Mono", monospace'

type ComponentStatus = 'ready' | 'in-progress'

type DocumentationSection = {
  key: string
  title: string
  status: ComponentStatus
  progress: number
  preview: JSX.Element
  code: string
  details: JSX.Element
}

const componentStatusMeta: Record<ComponentStatus, { label: string; icon: JSX.Element }> = {
  ready: {
    label: 'Done',
    icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 26 }} />
  },
  'in-progress': {
    label: 'In Progress',
    icon: <ClockCircleOutlined style={{ color: '#64748b' }} />
  }
}

export function UIKitPage(): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(null)
  const [refreshCount, setRefreshCount] = useState(0)
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null)
  const [gridSearch, setGridSearch] = useState('')
  const [gridCurrent, setGridCurrent] = useState(1)
  const [gridPageSize, setGridPageSize] = useState(10)
  const [standalonePagination, setStandalonePagination] = useState({ current: 1, pageSize: 10 })

  function handleManualRefresh(): void {
    setRefreshCount(count => count + 1)
  }

  const gridData = useMemo(
    () => [
      {
        id: '100157',
        group: '100157',
        name: 'Town of Centerville',
        location: 'TN',
        amount: '$494.00',
        status: 'CR Report Compiled'
      },
      {
        id: '100160',
        group: '100160',
        name: 'Basney Dealer Group',
        location: 'TN',
        amount: '$7,685.73',
        status: 'CR Report Compiled'
      },
      {
        id: '100287',
        group: '100287',
        name: 'Home Lumber of New Haven, Inc.',
        location: 'TN',
        amount: '$1,026.24',
        status: 'Waiting for Deposit'
      },
      {
        id: '100374',
        group: '100374',
        name: 'El Mariachi Restaurant',
        location: 'TN',
        amount: '$0.00',
        status: 'CR Report Compiled'
      },
      {
        id: '100417',
        group: '100417',
        name: 'Colorado Hospitality Services',
        location: 'CO',
        amount: '$33,055.04',
        status: 'CR Report Compiled'
      },
      {
        id: '100428',
        group: '100428',
        name: 'La Estrella Home Care',
        location: 'TN',
        amount: '—',
        status: 'Pending'
      },
      {
        id: '100463',
        group: '100463',
        name: 'Tekno, Inc.',
        location: 'TN',
        amount: '$4,176.20',
        status: 'CR Report Compiled'
      },
      {
        id: '100473',
        group: '100473',
        name: 'Patterson Warehouses, Inc.',
        location: 'TN',
        amount: '$11,057.22',
        status: 'Auto Release'
      },
      {
        id: '100475',
        group: '100475',
        name: 'Heritage Builders',
        location: 'TN',
        amount: '—',
        status: 'Pending'
      },
      {
        id: '100491',
        group: '100491',
        name: 'Trifecta Ventures, LLC',
        location: 'TN',
        amount: '$8,065.07',
        status: 'CR Report Compiled'
      },
      {
        id: '100502',
        group: '100502',
        name: 'Red Brick Analytics',
        location: 'TN',
        amount: '$1,870.54',
        status: 'CR Report Compiled'
      },
      {
        id: '100512',
        group: '100512',
        name: 'Northwind Test Group',
        location: 'WA',
        amount: '$6,211.83',
        status: 'Reviewing'
      }
    ],
    []
  )

  useEffect(() => {
    setGridCurrent(1)
  }, [gridSearch, gridPageSize])

  const filteredGridData = useMemo(() => {
    const query = gridSearch.trim().toLowerCase()

    if (!query) {
      return gridData
    }

    return gridData.filter(row =>
      [row.group, row.name, row.location, row.status].some(field =>
        field.toLowerCase().includes(query)
      )
    )
  }, [gridData, gridSearch])

  const pagedGridData = useMemo(() => {
    const start = (gridCurrent - 1) * gridPageSize
    return filteredGridData.slice(start, start + gridPageSize)
  }, [filteredGridData, gridCurrent, gridPageSize])

  const gridColumns = useMemo<ColumnsType<(typeof gridData)[number]>>(
    () => [
      { title: 'Group #', dataIndex: 'group', key: 'group', width: 120 },
      { title: 'Group Name', dataIndex: 'name', key: 'name', ellipsis: true },
      { title: 'Location', dataIndex: 'location', key: 'location', width: 120 },
      { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', width: 140 },
      { title: 'Status', dataIndex: 'status', key: 'status', width: 180 }
    ],
    []
  )

  const totalGridItems = filteredGridData.length
  const overallGridItems = gridData.length

  function handleGridPaginationChange(page: number, pageSize: number): void {
    setGridCurrent(page)
    setGridPageSize(pageSize)
  }

  function handleStandalonePaginationChange(page: number, pageSize: number): void {
    setStandalonePagination({ current: page, pageSize })
  }

  const activeRefreshLabel = useMemo(() => {
    if (!autoRefreshInterval) {
      return 'Manual mode'
    }

    const option = searchRefreshOptions.find(item => item.value === autoRefreshInterval)

    if (!option) {
      return 'Custom interval'
    }

    return option.label
  }, [autoRefreshInterval])

  const documentationSections: DocumentationSection[] = [
    {
      key: 'container',
      title: '<PageContainer />',
      status: 'ready',
      progress: 100,
      preview: (
        <div className='p-4 w-full rounded-2xl bg-slate-100 sm:p-6 lg:p-8'>
          <PageContainer className='mx-auto w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl'>
            <Title level={5} className='!mb-2' style={{ fontFamily: headingFontFamily }}>
              Content inside PageContainer
            </Title>
            <Paragraph type='secondary' className='!mb-0'>
              Use the component to provide a consistent background, 26px border radius, margins and
              shadow
            </Paragraph>
          </PageContainer>
        </div>
      ),
      code: `<PageContainer className='max-w-xl'>
  {/* page content */}
</PageContainer>`,
      details: (
        <Fragment>
          <Paragraph>
            Wrap the main page content in
            <Text className='font-bold'> PageContainer</Text> to ensure consistent styling
            <Text code>white</Text> background,
            <Text code>26px</Text> border radius, blurred shadow, and <Text code>p-8</Text> padding.
          </Paragraph>
          <Paragraph>
            For a specific use case, you can add Tailwind classes or inline styles to adjust sizes
            and spacing, as the component accepts all standard <Text code>div</Text> properties.
          </Paragraph>
          <Paragraph type='secondary'>
            Path: <Tag color='blue'>frontend/src/components/page/page-container.tsx</Tag>
          </Paragraph>
        </Fragment>
      )
    },
    {
      key: 'header',
      title: '<PageHeader />',
      status: 'ready',
      progress: 100,
      preview: (
        <PageHeader
          title='Release Payment Files'
          description='The title can be generated from the menu or specified manually'
          icon={<FileProtectOutlined />}
          breadcrumbs={[
            { key: 'register', label: 'Register', to: '/register' },
            { key: 'release', label: 'Release Payment Files' }
          ]}
        />
      ),
      code: `<PageHeader
  title='Release Payment Files'
  description='Optional description'
  icon={<FileProtectOutlined />}
  breadcrumbs={[
    { key: 'register', label: 'Register', to: '/register' },
    { key: 'release', label: 'Release Payment Files' }
  ]}
/>
// or without props, if the page exists in the navigation menu`,
      details: (
        <Fragment>
          <Paragraph>
            By default, <Text className='font-bold'>PageHeader </Text>
            looks up the current route in the navigation menu structure and takes the
            <Text code>title</Text>, <Text code>icon</Text>, and <Text code>breadcrumbs</Text> from
            there. For custom screens, you can provide these values manually.
          </Paragraph>
          <Paragraph>
            The <Text code>hideIcon</Text> and <Text code>breadcrumbSeparator</Text> flags help you
            flexibly control the appearance, while the <Text code>breadcrumbs</Text> prop accepts an
            array of keys and links, allowing each segment to be styled with a separate class.
          </Paragraph>
          <Paragraph type='secondary'>
            Path: <Tag color='blue'>frontend/src/components/page/page-header.tsx</Tag>
          </Paragraph>
        </Fragment>
      )
    },
    {
      key: 'icon-button',
      title: '<IconButton />',
      status: 'in-progress',
      progress: 80,
      preview: (
        <div className='flex flex-wrap gap-3 justify-center items-center sm:justify-start'>
          <IconButton type='primary' icon={<PlusOutlined />}>
            Create
          </IconButton>
          <IconButton icon={<CopyOutlined />} iconPosition='right'>
            Duplicate
          </IconButton>
          <IconButton type='text' danger icon={<DeleteOutlined />}>
            Delete
          </IconButton>
        </div>
      ),
      code: `<IconButton type='primary' icon={<PlusOutlined />}>
  Create
</IconButton>
<IconButton icon={<CopyOutlined />} iconPosition='right'>
  Duplicate
</IconButton>
<IconButton type='text' danger icon={<DeleteOutlined />}>
  Delete
</IconButton>`,
      details: (
        <Fragment>
          <Paragraph>
            <Text className='font-bold'>IconButton</Text> wraps the standard
            <Text code>Button</Text> component from Ant Design and centers the alignment of the icon
            and text. You can pass any JSX into the<Text code>icon</Text> prop, and use{' '}
            <Text code>iconPosition</Text> to quickly switch the icon’s placement.
          </Paragraph>
          <Paragraph>
            All standard Ant Design properties are supported — <Text code>type</Text>,
            <Text code>danger</Text>, <Text code>block</Text>, <Text code>loading</Text> and others
            — making the button easy to adapt for different use cases.
          </Paragraph>
          <Paragraph type='secondary'>
            Path: <Tag color='blue'>frontend/src/components/ui/icon-button.tsx</Tag>
          </Paragraph>
        </Fragment>
      )
    },
    {
      key: 'search-toolbar',
      title: '<PageSearchToolbar />',
      status: 'in-progress',
      progress: 60,
      preview: (
        <PageSearchToolbar
          value={searchQuery}
          onChange={setSearchQuery}
          onRefresh={handleManualRefresh}
          refreshInterval={autoRefreshInterval}
          onRefreshIntervalChange={setAutoRefreshInterval}
          refreshOptions={searchRefreshOptions}
          refreshTooltip={`Click to refresh now. Auto: ${activeRefreshLabel}`}
          extraActions={
            <Button type='primary' icon={<PlusOutlined />}>
              Add action
            </Button>
          }
        />
      ),
      code: `<PageSearchToolbar
  value={searchQuery}
  onChange={setSearchQuery}
  onRefresh={refetch}
  refreshInterval={autoRefreshInterval}
  onRefreshIntervalChange={setAutoRefreshInterval}
  extraActions={<Button icon={<PlusOutlined />}>New</Button>}
/>`,
      details: (
        <Fragment>
          <Paragraph>
            The <Text className='font-bold'>PageSearchToolbar</Text> component combines a search
            field and a refresh button. Clicking the main button triggers
            <Text code>onRefresh</Text>, while the dropdown menu allows enabling auto-refresh with a
            specified interval. The list of intervals is configured via{' '}
            <Text code>refreshOptions</Text>.
          </Paragraph>
          <Paragraph type='secondary'>
            The field currently contains: <Text strong>{searchQuery || '—'}</Text>. Refresh has been
            triggered
            <Text strong> {refreshCount}</Text> time(s). Active mode:{' '}
            <Text strong>{activeRefreshLabel}</Text>.
          </Paragraph>
          <Paragraph type='secondary'>
            Path: <Tag color='blue'>frontend/src/components/page/page-search-toolbar.tsx</Tag>
          </Paragraph>
        </Fragment>
      )
    },
    {
      key: 'footer',
      title: '<PageFooter />',
      status: 'ready',
      progress: 100,
      preview: (
        <div className='p-6 w-full rounded-2xl bg-slate-100'>
          <PageFooter className='ml-auto max-w-xl text-right' />
        </div>
      ),
      code: `<PageFooter className='text-right' />`,
      details: (
        <Fragment>
          <Paragraph>
            The application version and copyright are displayed in a compact footer without
            background fill. Use it inside
            <Text code>PageContainer</Text> or separately by passing classes through{' '}
            <Text code>className</Text>.
          </Paragraph>
          <Paragraph>
            The component replicates the behavior of the global <Text code>{'<Footer />'}</Text>,
            but does not apply a background color, making it convenient for internal pages with
            custom layout solutions.
          </Paragraph>
          <Paragraph type='secondary'>
            Path: <Tag color='blue'>frontend/src/components/page/page-footer.tsx</Tag>
          </Paragraph>
        </Fragment>
      )
    },
    {
      key: 'grid',
      title: '<PageGrid />',
      status: 'in-progress',
      progress: 50,
      preview: (
        <PageGrid
          toolbar={
            <PageSearchToolbar
              value={gridSearch}
              onChange={setGridSearch}
              placeholder='Filter groups by name, number, or status'
              onRefresh={() => handleGridPaginationChange(1, gridPageSize)}
              refreshTooltip='Reset to the first page'
              refreshInterval={null}
              extraActions={
                <Button size='small' icon={<PlusOutlined />}>
                  New group
                </Button>
              }
            />
          }
          columns={gridColumns}
          dataSource={pagedGridData}
          rowKey='id'
          pagination={{
            current: gridCurrent,
            pageSize: gridPageSize,
            total: totalGridItems,
            overallTotal: overallGridItems,
            onChange: handleGridPaginationChange
          }}
        />
      ),
      code: `<PageGrid
  toolbar={
    <PageSearchToolbar
      value={search}
      onChange={setSearch}
      onRefresh={() => refetch()}
    />
  }
  columns={columns}
  dataSource={rows}
  rowKey='id'
  pagination={{
    current,
    pageSize,
    total,
    overallTotal,
    onChange: handleTableChange
  }}
/>
`,
      details: (
        <Fragment>
          <Paragraph>
            <Text className='font-bold'>PageGrid</Text> wraps the Ant Design table with the rounded
            white card layout used across Release Payment Files and Premium Groups. It exposes
            optional <Text code>toolbar</Text> and <Text code>footerArea</Text> slots so you can
            drop in filters, actions, or summary widgets without rewriting layout code.
          </Paragraph>
          <Paragraph>
            Set the <Text code>pagination</Text> prop to enable the shared footer experience and let
            the component forward the rest of the table props directly to Ant Design for sorting,
            selection, expandable rows, and custom cell renderers.
          </Paragraph>
          <Paragraph type='secondary'>
            Path: <Tag color='blue'>frontend/src/components/page/page-grid.tsx</Tag>
          </Paragraph>
        </Fragment>
      )
    },
    {
      key: 'grid-pagination',
      title: '<PageGridPagination />',
      status: 'ready',
      progress: 50,
      preview: (
        <div className='p-6 w-full bg-white rounded-2xl border border-dashed border-slate-300'>
          <PageGridPagination
            current={standalonePagination.current}
            pageSize={standalonePagination.pageSize}
            total={7921}
            overallTotal={9539}
            onChange={handleStandalonePaginationChange}
            itemsLabel='items'
          />
        </div>
      ),
      code: `<PageGridPagination
  current={page}
  pageSize={pageSize}
  total={filteredTotal}
  overallTotal={overallTotal}
  onChange={(nextPage, nextSize) => {
    setPage(nextPage)
    setPageSize(nextSize)
  }}
/>
`,
      details: (
        <Fragment>
          <Paragraph>
            Use <Text className='font-bold'>PageGridPagination</Text> whenever you need the Lucent
            summary — “Showing X–Y of Z items • Total …” — coupled with Ant Design pagination. The
            component formats numbers automatically and exposes a <Text code>summary</Text> prop to
            override the default text when necessary.
          </Paragraph>
          <Paragraph>
            It pairs naturally with <Text code>PageGrid</Text>, but you can also drop it beneath any
            dataset that requires consistent pagination controls and textual totals.
          </Paragraph>
          <Paragraph type='secondary'>
            Path: <Tag color='blue'>frontend/src/components/page/page-grid-pagination.tsx</Tag>
          </Paragraph>
        </Fragment>
      )
    }
  ]

  const totalComponents = documentationSections.length

  return (
    <div className='flex flex-col'>
      <PageContainer className='p-4 w-full'>
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
            <PageHeader
              title='UI Kit Playground'
              description='Reusable page‑level building blocks shown together for quick review.'
              icon={<AppstoreOutlined />}
              breadcrumbs={[
                { key: 'playground', label: 'Design System' },
                { key: 'uikit', label: 'UI Kit Playground' }
              ]}
            />
            <div className='flex w-full min-w-[100px] flex-col items-center gap-0.5 rounded-2xl bg-slate-100 px-5 py-3 text-center sm:w-auto'>
              <Text type='secondary' className='text-xs tracking-wide text-slate-500'>
                Available components
              </Text>
              <div
                className='p-1 text-2xl font-semibold leading-tight text-slate-900'
                style={{ fontFamily: headingFontFamily }}
              >
                {totalComponents}
              </div>
            </div>
          </div>

          <section className='flex flex-col gap-6'>
            {documentationSections.map(section => (
              <div
                key={section.key}
                className='flex flex-col gap-6 p-4 rounded-3xl border border-slate-200 bg-white/80 sm:p-6 lg:p-8'
              >
                <div className='grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]'>
                  <div className='flex justify-center items-start w-full lg:justify-start'>
                    {section.preview}
                  </div>
                  <div className='flex flex-col gap-4 min-w-0'>
                    <div className='flex gap-3 justify-between items-center'>
                      <Title
                        level={4}
                        className='!mb-0 flex-1'
                        style={{ fontFamily: headingFontFamily }}
                      >
                        {section.title}
                      </Title>
                      {section.progress === 100 ? (
                        <Tooltip
                          title={componentStatusMeta.ready.label}
                          placement='right'
                          style={{
                            fontSize: 12
                          }}
                        >
                          <span className='flex items-center text-slate-600'>
                            {componentStatusMeta.ready.icon}
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={`${componentStatusMeta[section.status].label} · ${section.progress}%`}
                          placement='right'
                          style={{
                            fontSize: 12
                          }}
                        >
                          <Progress
                            type='circle'
                            percent={section.progress}
                            size={26}
                            strokeWidth={18}
                            strokeColor='#1677ff'
                            trailColor='rgba(148, 163, 184, 0.3)'
                            status='normal'
                            format={() => null}
                            className='!m-0'
                          />
                        </Tooltip>
                      )}
                    </div>
                    <div className='relative'>
                      <pre
                        className='max-w-full overflow-x-auto rounded-xl bg-slate-50 border border-slate-200 p-4 font-mono text-[13px] leading-6'
                        dangerouslySetInnerHTML={{
                          __html: hljs.highlight(section.code, { language: 'typescript' }).value
                        }}
                      />

                      <Tooltip
                        open={copiedSnippet === section.key}
                        title='Copied'
                        placement='left'
                        destroyOnHidden
                        style={{
                          fontSize: 12
                        }}
                      >
                        <Button
                          size='small'
                          type='text'
                          icon={<CopyOutlined />}
                          className='!absolute top-3 right-3 z-10 !px-3.5 !h-8 !rounded-lg !border !text-white hover:!text-white focus:!text-white !bg-white/10 hover:!bg-white/20 active:!scale-90 transition duration-150 ease-out'
                          style={{ borderWidth: 1.5 }}
                          onClick={() => {
                            void navigator.clipboard.writeText(section.code).then(() => {
                              setCopiedSnippet(section.key)
                              window.setTimeout(() => {
                                setCopiedSnippet(prev => (prev === section.key ? null : prev))
                              }, 1500)
                            })
                          }}
                        />
                      </Tooltip>
                    </div>
                    {section.details}
                  </div>
                </div>
              </div>
            ))}
          </section>
        </div>
      </PageContainer>
    </div>
  )
}

export default UIKitPage
